import { Adaptation } from '../../types';

export const mockAdaptations: Adaptation[] = [
  {
    id: '1',
    name: 'Muscular Hypertrophy',
    category: 'Hypertrophy',
    description: 'An increase in muscle fiber size through protein synthesis, resulting in increased muscle mass and cross-sectional area.',
    scientificParameters: [
      'Training volume: 10-20 sets per muscle group per week',
      'Intensity: 60-85% 1RM',
      'Rep range: 6-15 repetitions',
      'Rest periods: 1-3 minutes',
      'Frequency: 2-3 times per week per muscle group',
      'Progressive overload: Gradual increase in volume, intensity, or frequency'
    ],
    exerciseAssociations: ['compound-movements', 'isolation-exercises', 'machine-exercises'],
  },
  {
    id: '2',
    name: 'Maximal Strength',
    category: 'Strength',
    description: 'The maximum force a muscle or muscle group can generate in a single voluntary contraction.',
    scientificParameters: [
      'Intensity: 85-100% 1RM',
      'Rep range: 1-5 repetitions',
      'Sets: 3-6 sets',
      'Rest periods: 3-5 minutes',
      'Frequency: 2-3 times per week',
      'Neural adaptations: Motor unit recruitment and firing frequency'
    ],
    exerciseAssociations: ['powerlifting-movements', 'compound-exercises', 'free-weights'],
  },
  {
    id: '3',
    name: 'Cardiovascular Endurance',
    category: 'Endurance',
    description: 'The ability of the heart, lungs, and blood vessels to deliver oxygen and nutrients to working muscles during prolonged exercise.',
    scientificParameters: [
      'Intensity: 50-85% HRmax',
      'Duration: 20-60+ minutes',
      'Frequency: 3-6 times per week',
      'Mode: Continuous or interval training',
      'VO2max improvements: 15-25% in untrained individuals',
      'Mitochondrial adaptations: Increased density and enzyme activity'
    ],
    exerciseAssociations: ['running', 'cycling', 'swimming', 'rowing'],
  },
  {
    id: '4',
    name: 'Muscular Power',
    category: 'Power',
    description: 'The ability to generate force rapidly, combining strength and speed components for explosive movements.',
    scientificParameters: [
      'Intensity: 30-90% 1RM (velocity-dependent)',
      'Rep range: 1-6 repetitions',
      'Rest periods: 2-5 minutes',
      'Movement velocity: Maximum intended speed',
      'Frequency: 2-4 times per week',
      'Rate of force development: Primary adaptation marker'
    ],
    exerciseAssociations: ['olympic-lifts', 'plyometrics', 'ballistic-movements'],
  },
  {
    id: '5',
    name: 'Muscular Endurance',
    category: 'Endurance',
    description: 'The ability of a muscle or muscle group to perform repeated contractions against resistance over time.',
    scientificParameters: [
      'Intensity: 40-70% 1RM',
      'Rep range: 15-50+ repetitions',
      'Rest periods: 30-90 seconds',
      'Frequency: 2-4 times per week',
      'Metabolic adaptations: Improved lactate buffering',
      'Capillarization: Increased capillary density'
    ],
    exerciseAssociations: ['high-rep-training', 'circuit-training', 'bodyweight-exercises'],
  },
  {
    id: '6',
    name: 'Flexibility',
    category: 'Flexibility',
    description: 'The range of motion available at a joint or series of joints, influenced by muscle length and joint structure.',
    scientificParameters: [
      'Duration: 15-60 seconds per stretch',
      'Frequency: Daily or 2-3 times per week',
      'Intensity: To point of mild discomfort',
      'Mode: Static, dynamic, or PNF stretching',
      'Temperature: Performed when muscles are warm',
      'Progression: Gradual increase in range of motion'
    ],
    exerciseAssociations: ['static-stretching', 'dynamic-stretching', 'yoga', 'pnf-stretching'],
  },
];

export const mockAdaptationStats = {
  total: mockAdaptations.length,
  categories: new Set(mockAdaptations.map(a => a.category)).size,
  avgParameters: mockAdaptations.length > 0 
    ? mockAdaptations.reduce((sum, a) => sum + a.scientificParameters.length, 0) / mockAdaptations.length 
    : 0,
  evidenceBase: 92,
};

export const mockCategoryProgress = [
  {
    title: 'Strength',
    percent: (mockAdaptations.filter(a => a.category === 'Strength').length / mockAdaptations.length) * 100,
    status: 'success' as const,
  },
  {
    title: 'Hypertrophy',
    percent: (mockAdaptations.filter(a => a.category === 'Hypertrophy').length / mockAdaptations.length) * 100,
    status: 'success' as const,
  },
  {
    title: 'Endurance',
    percent: (mockAdaptations.filter(a => a.category === 'Endurance').length / mockAdaptations.length) * 100,
    status: 'normal' as const,
  },
  {
    title: 'Power',
    percent: (mockAdaptations.filter(a => a.category === 'Power').length / mockAdaptations.length) * 100,
    status: 'normal' as const,
  },
  {
    title: 'Flexibility',
    percent: (mockAdaptations.filter(a => a.category === 'Flexibility').length / mockAdaptations.length) * 100,
    status: 'normal' as const,
  },
  {
    title: 'Balance',
    percent: 0,
    status: 'exception' as const,
  },
  {
    title: 'Coordination',
    percent: 0,
    status: 'exception' as const,
  },
  {
    title: 'Mobility',
    percent: 0,
    status: 'exception' as const,
  },
]; 